import { ContentPaste, PersonPin, Pets, Star, Transgender, Tune, Wc } from '@mui/icons-material'
import React, { FC, useEffect, useRef } from 'react'

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  InputAdornment,
  Pagination,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'

import { Add, Close, Search as SearchIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { accountCanEdit } from '../../domain/Account/accountCanEdit'
import {
  getReadableAnimalAdoptionStatus,
  getReadableAnimalAdoptionStep,
  getReadableCannotBeAdoptedStatus,
  getReadableSex,
  getReadableSpecies,
} from '../../domain/Animal/AnimalDisplay'
import { AnimalFilters } from '../../domain/Animal/AnimalFiltersType'
import { BooleanOrAll, BooleanOrUnknown } from '../../domain/enums/Boolean.enum'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import {
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDtoSex,
  AnimalDtoSpecies,
  AnimalSearchItemDto,
} from '../../interactors/gen/backendClient'
import { useAccountsStore } from '../../store/AccountsStore'
import { useAnimalStore } from '../../store/AnimalStore'
import { useFetchOrRefreshAnimalsOnMount } from '../../store/useFetchOrRefreshAnimalsOnMount'
import { useFetchOrRefreshMembersOnMount } from '../../store/useFetchOrRefreshMembersOnMount'
import { getReadableBooleanOrUnknown } from '../../utils/getReadableBooleanOrUnknown'
import { useComponentScrollRestoration } from '../../utils/hooks/useScrollRestoration'
import { PATHS } from '../PATHS'
import { AQuestion } from '../common/AQuestion'
import { CircularProgressPanel } from '../common/CircularProgressPanel'
import { FilterList } from '../common/FilterList'
import { FilterListItem } from '../common/FilterListItem'
import { blueSky, spacingItem } from '../theme'
import { AnimalExportPopUp } from './AnimalExportPopUp'
import { AnimalSheetPopUp } from './AnimalSheetPopUp'
import { AnimalsTable, SortColumn, SortDirection } from './AnimalsTable'

export type AnimalAdoptionDtoStepWithEmpty = AnimalAdoptionDtoStep | 'empty'
export type AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty = AnimalAdoptionDtoCannotBeAdoptedStatus | 'empty'

export const AnimalListScreen: FC = () => {
  const scrollRef = useRef<HTMLElement | null>(null)
  useComponentScrollRestoration(scrollRef, 'animal-list')

  const queryParams = new URLSearchParams(location.search)
  const getArrayQueryParamOrDefault = (paramValue: string | null | undefined, defaultValue: string[]) => {
    const values = paramValue?.split('|')
    if (values && values.length === 1 && values[0] === '') {
      return defaultValue
    }
    return values ?? defaultValue
  }

  const [selectedAnimals, setSelectedAnimals] = React.useState<AnimalSearchItemDto[]>([])

  // Filters
  const [myAnimalsOnly, setMyAnimalsOnly] = React.useState(queryParams.get('myAnimalsOnly') === 'true')
  const [hasIdentificationNumberFilter, setHasIdentificationNumberFilter] = React.useState(
    (queryParams.get('hasIdentificationNumberFilter') as BooleanOrAll) ?? BooleanOrAll.All
  )
  const [sterilizedOrNeuteredFilter, setSterilizedOrNeuteredFilter] = React.useState<BooleanOrUnknown[]>(
    getArrayQueryParamOrDefault(queryParams.get('sterilizedOrNeuteredFilter'), []) as BooleanOrUnknown[]
  )
  const [adoptionStatusFilter, setAdoptionStatusFilter] = React.useState<AnimalAdoptionDtoStatus[]>(
    getArrayQueryParamOrDefault(queryParams.get('adoptionStatusFilter'), [
      AnimalAdoptionDtoStatus.Adoptable,
      AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
      AnimalAdoptionDtoStatus.CannotBeAdopted,
    ]) as AnimalAdoptionDtoStatus[]
  )
  const [speciesFilter, setSpeciesFilter] = React.useState<AnimalDtoSpecies[]>(
    getArrayQueryParamOrDefault(queryParams.get('speciesFilter'), []) as AnimalDtoSpecies[]
  )
  /* const [hasExitDateFilter, setHasExitDateFilter] = React.useState<boolean | null>(null) */
  const [vpaDateFilter, setVpaDateFilter] = React.useState<'past' | 'future' | 'empty' | null>(
    (queryParams.get('vpaDateFilter') as 'past' | 'future' | 'empty' | null) ?? null
  )
  const [sexFilter, setSexFilter] = React.useState<AnimalDtoSex | null>(
    queryParams.get('sexFilter') as AnimalDtoSex | null
  )
  const [stepFilter, setStepFilter] = React.useState<AnimalAdoptionDtoStepWithEmpty[]>(
    getArrayQueryParamOrDefault(queryParams.get('stepFilter'), [
      ...Object.values(AnimalAdoptionDtoStep),
      'empty',
    ]) as AnimalAdoptionDtoStepWithEmpty[]
  )
  const [cannotBeAdoptedStatusFilter, setCannotBeAdoptedStatusFilter] = React.useState<
    AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[]
  >(
    getArrayQueryParamOrDefault(queryParams.get('cannotBeAdoptedStatusFilter'), [
      AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized,
      AnimalAdoptionDtoCannotBeAdoptedStatus.InCare,
      AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation,
      AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation,
      AnimalAdoptionDtoCannotBeAdoptedStatus.Released,
      AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily,
      'empty',
    ]) as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[]
  )
  const createFilterObject = (): AnimalFilters => {
    return {
      myAnimalsOnly,
      hasIdentificationNumberFilter,
      sterilizedOrNeuteredFilter,
      adoptionStatusFilter,
      stepFilter,
      cannotBeAdoptedStatusFilter,
      hasExitDateFilter: null,
      vpaDateFilter,
      speciesFilter,
      sexFilter,
    }
  }
  const baseFilterObject = {
    myAnimalsOnly: false,
    hasIdentificationNumberFilter: 'all',
    sterilizedOrNeuteredFilter: [],
    adoptionStatusFilter: [],
    stepFilter: [
      'animal-reserved',
      'adoption-to-be-validated',
      'adoption-validated-without-post-visit',
      'adoption-validated-after-post-visit',
      'post-visit-not-validated',
      'animal-returned',
      'empty',
    ],
    cannotBeAdoptedStatusFilter: [
      'released',
      'dead',
      'long-term-with-hosted-family',
      'in-care',
      'being-socialized',
      'lost',
      'transfer-to-another-association',
      'in-gestation',
      'in-lactation',
      'empty',
    ],
    hasExitDateFilter: null,
    vpaDateFilter: null,
    speciesFilter: [],
    sexFilter: null,
  }

  const [selectedTab, setSelectedTab] = React.useState<string>(queryParams.get('selectedTab') ?? 'followed')

  const [sortColumn, setSortColumn] = React.useState<SortColumn>(
    (queryParams.get('sortColumn') ?? 'insights') as SortColumn
  )
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(
    (queryParams.get('sortDirection') ?? 'asc') as SortDirection
  )

  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [page, setPage] = React.useState(parseInt(queryParams.get('page') || '1', 10))
  const [assoHasAnimals, setAssoHasAnimals] = React.useState<boolean>(true)
  const [searchQuery, setSearchQuery] = React.useState(queryParams.get('search') || '')
  const [appliedSearchQuery, setAppliedSearchQuery] = React.useState(queryParams.get('search') || '')
  const maxAnimalPerPage = 50
  const isSearchDirty = searchQuery.trim() !== appliedSearchQuery
  const hasAppliedSearch = appliedSearchQuery !== ''

  const animalStore = useAnimalStore()
  const accountsStore = useAccountsStore()
  const navigate = useNavigate()

  const account = accountsStore.connectedAccount

  useEffect(() => {
    const searchParams = new URLSearchParams()
    searchParams.set('page', page.toString())
    searchParams.set('search', appliedSearchQuery)
    searchParams.set('myAnimalsOnly', myAnimalsOnly.toString())
    searchParams.set('hasIdentificationNumberFilter', hasIdentificationNumberFilter)
    searchParams.set('sterilizedOrNeuteredFilter', sterilizedOrNeuteredFilter.join('|'))
    searchParams.set('adoptionStatusFilter', adoptionStatusFilter.join('|'))
    searchParams.set('stepFilter', stepFilter.join('|'))
    searchParams.set('cannotBeAdoptedStatusFilter', cannotBeAdoptedStatusFilter.join('|'))
    searchParams.set('vpaDateFilter', vpaDateFilter ?? '')
    searchParams.set('speciesFilter', speciesFilter.join('|'))
    searchParams.set('sexFilter', sexFilter ?? '')
    searchParams.set('sortColumn', sortColumn)
    searchParams.set('sortDirection', sortDirection)
    searchParams.set('selectedTab', selectedTab)
    // Add more parameters as needed
    navigate(`?${searchParams.toString()}`, { replace: true })
  }, [
    page,
    appliedSearchQuery,
    myAnimalsOnly,
    hasIdentificationNumberFilter,
    sterilizedOrNeuteredFilter,
    adoptionStatusFilter,
    stepFilter,
    cannotBeAdoptedStatusFilter,
    speciesFilter,
    sexFilter,
    sortColumn,
    sortDirection,
    selectedTab,
  ])

  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false // Mark as rendered
      return // Skip this effect on first render
    }

    setPage(1) // Reset page to 1 whenever filter values change
  }, [
    myAnimalsOnly,
    hasIdentificationNumberFilter,
    sterilizedOrNeuteredFilter,
    adoptionStatusFilter,
    stepFilter,
    cannotBeAdoptedStatusFilter,
    vpaDateFilter,
    speciesFilter,
    sexFilter,
  ])

  useEffect(() => {
    AnimalsClient.countAllAnimal().then((count) => {
      if (count > 0) {
        setAssoHasAnimals(true)
      } else {
        setAssoHasAnimals(false)
      }
    })
  }, [])

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const nextValue = e.target.value
    setSearchQuery(nextValue)
    if (nextValue.trim() === '' && appliedSearchQuery !== '') {
      setAppliedSearchQuery('')
      setPage(1)
    }
  }

  useFetchOrRefreshMembersOnMount()
  useFetchOrRefreshAnimalsOnMount(
    page,
    maxAnimalPerPage,
    createFilterObject(),
    appliedSearchQuery,
    sortColumn,
    sortDirection
  )

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleTabChange = (_: unknown, newValue: string) => {
    setSelectedTab(newValue)
    let adoptionFilterValues: AnimalAdoptionDtoStatus[] = []
    let cannotBeAdoptedFilterValues: AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty[] = []
    switch (newValue) {
      case 'all':
        adoptionFilterValues = []
        cannotBeAdoptedFilterValues = [...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus), 'empty']
        break
      case 'followed':
        adoptionFilterValues = [
          AnimalAdoptionDtoStatus.Adoptable,
          AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
          AnimalAdoptionDtoStatus.CannotBeAdopted,
        ]
        cannotBeAdoptedFilterValues = [
          AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized,
          AnimalAdoptionDtoCannotBeAdoptedStatus.InCare,
          AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation,
          AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation,
          AnimalAdoptionDtoCannotBeAdoptedStatus.Released,
          AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily,
          'empty',
        ]
        break
      case 'to-be-filled':
        adoptionFilterValues = [AnimalAdoptionDtoStatus.ToBeFilled]
        cannotBeAdoptedFilterValues = []
        break
      case 'archived':
        adoptionFilterValues = [AnimalAdoptionDtoStatus.HasBeenAdopted, AnimalAdoptionDtoStatus.CannotBeAdopted]
        cannotBeAdoptedFilterValues = [
          AnimalAdoptionDtoCannotBeAdoptedStatus.Dead,
          AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation,
          AnimalAdoptionDtoCannotBeAdoptedStatus.Lost,
        ]
        break
    }
    setAdoptionStatusFilter(adoptionFilterValues)
    setCannotBeAdoptedStatusFilter(cannotBeAdoptedFilterValues)
  }

  const handleApplySearch = (nextQuery?: string) => {
    const query = (nextQuery ?? searchQuery).trim()
    setSearchQuery(query)
    setAppliedSearchQuery(query)
    setPage(1)
    if (query !== '') {
      handleTabChange(null, 'all')
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    if (appliedSearchQuery !== '') {
      setAppliedSearchQuery('')
      setPage(1)
    }
  }

  useEffect(() => {
    if (queryParams.get('selectedTab') != null && queryParams.get('selectedTab') !== selectedTab) {
      handleTabChange(null, queryParams.get('selectedTab') ?? 'followed')
    }
  }, [queryParams.get('selectedTab')])

  const FiltersBar = () => {
    return (
      <>
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 1.25,
            mb: 0.5,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <Typography
            sx={{
              mb: 0.8,
              fontSize: '0.78rem',
              fontWeight: 700,
              color: 'text.secondary',
              textAlign: 'center',
              lineHeight: 1.25,
            }}
          >
            Nouvelle interface disponible !
          </Typography>
          <Button
            href="https://beta.petso.fr/login"
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              py: 0.55,
              color: blueSky,
              borderColor: blueSky,
              '&:hover': {
                borderColor: blueSky,
                backgroundColor: 'rgba(109, 165, 227, 0.06)',
              },
            }}
          >
            Découvrir &gt;
          </Button>
        </Box>
        <FilterList label="Résponsable" icon={<Star />}>
          <FilterListItem
            label="Mes animaux"
            isSelected={myAnimalsOnly}
            toggleSelected={() => setMyAnimalsOnly(!myAnimalsOnly)}
          />
        </FilterList>

        <FilterList label="Identification" icon={<ContentPaste />}>
          <FilterListItem
            label="Immatriculé"
            isSelected={hasIdentificationNumberFilter === BooleanOrAll.True}
            toggleSelected={() => {
              if (hasIdentificationNumberFilter === BooleanOrAll.True) {
                setHasIdentificationNumberFilter(BooleanOrAll.All)
              } else {
                setHasIdentificationNumberFilter(BooleanOrAll.True)
              }
            }}
          />
          <FilterListItem
            label="Manquante"
            isSelected={hasIdentificationNumberFilter === BooleanOrAll.False}
            toggleSelected={() => {
              if (hasIdentificationNumberFilter === BooleanOrAll.False) {
                setHasIdentificationNumberFilter(BooleanOrAll.All)
              } else {
                setHasIdentificationNumberFilter(BooleanOrAll.False)
              }
            }}
          />
        </FilterList>

        <FilterList label="Sterilisée/Castré" icon={<Transgender />}>
          {Object.values(BooleanOrUnknown).map((componentStatus) => {
            return (
              <FilterListItem
                key={componentStatus}
                label={getReadableBooleanOrUnknown(componentStatus)}
                isSelected={sterilizedOrNeuteredFilter.includes(componentStatus)}
                toggleSelected={() => {
                  if (sterilizedOrNeuteredFilter.includes(componentStatus)) {
                    const newFilter = sterilizedOrNeuteredFilter.filter((aStatus) => aStatus !== componentStatus)
                    setSterilizedOrNeuteredFilter(newFilter)
                  } else {
                    setSterilizedOrNeuteredFilter([...sterilizedOrNeuteredFilter, componentStatus])
                  }
                }}
              />
            )
          })}
        </FilterList>

        <FilterList label="Espèce" icon={<Pets />}>
          {accountsStore.connectedCustomer?.preferences.species.map((componentStatus) => {
            return (
              <FilterListItem
                key={componentStatus}
                label={getReadableSpecies(componentStatus)}
                isSelected={speciesFilter.includes(componentStatus)}
                toggleSelected={() => {
                  if (speciesFilter.includes(componentStatus)) {
                    const newFilter = speciesFilter.filter((aStatus) => aStatus !== componentStatus)
                    setSpeciesFilter(newFilter)
                  } else {
                    setSpeciesFilter([...speciesFilter, componentStatus])
                  }
                }}
              />
            )
          })}
        </FilterList>

        <FilterList label="Sexe" icon={<Wc />}>
          {Object.values(AnimalDtoSex).map((componentStatus) => (
            <FilterListItem
              key={componentStatus}
              label={getReadableSex(componentStatus)}
              isSelected={sexFilter === componentStatus}
              toggleSelected={() => {
                if (sexFilter === componentStatus) {
                  setSexFilter(null)
                } else {
                  setSexFilter(componentStatus)
                }
              }}
            />
          ))}
        </FilterList>

        <FilterList label="Post-visite" icon={<PersonPin />}>
          <FilterListItem
            label="Passée"
            isSelected={vpaDateFilter === 'past'}
            toggleSelected={() => setVpaDateFilter(vpaDateFilter === 'past' ? null : 'past')}
          />
          <FilterListItem
            label="À venir"
            isSelected={vpaDateFilter === 'future'}
            toggleSelected={() => setVpaDateFilter(vpaDateFilter === 'future' ? null : 'future')}
          />
          <FilterListItem
            label="Vide"
            isSelected={vpaDateFilter === 'empty'}
            toggleSelected={() => setVpaDateFilter(vpaDateFilter === 'empty' ? null : 'empty')}
          />
        </FilterList>

        <FilterList label="Statut" icon={<PersonPin />}>
          {Object.values(AnimalAdoptionDtoStatus).map((componentStatus) => {
            return (
              <FilterListItem
                key={componentStatus}
                label={getReadableAnimalAdoptionStatus(componentStatus)}
                isSelected={adoptionStatusFilter.includes(componentStatus)}
                toggleSelected={() => {
                  if (adoptionStatusFilter.includes(componentStatus)) {
                    if (componentStatus === AnimalAdoptionDtoStatus.CannotBeAdopted) {
                      // Set all "cannot-be-adopted" statuses
                      setCannotBeAdoptedStatusFilter(Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus))
                    }
                    if (componentStatus === AnimalAdoptionDtoStatus.HasBeenAdopted) {
                      // Set all "has-been-adopted" statuses
                      setStepFilter(Object.values(AnimalAdoptionDtoStep))
                    }
                    const newFilter = adoptionStatusFilter.filter((aStatus) => aStatus !== componentStatus)
                    setAdoptionStatusFilter(newFilter)
                  } else {
                    setAdoptionStatusFilter([...adoptionStatusFilter, componentStatus])
                  }
                }}
              />
            )
          })}
          {adoptionStatusFilter.includes('has-been-adopted') && (
            <FilterList label="État de l'adoption" icon={<PersonPin />}>
              {[...Object.values(AnimalAdoptionDtoStep), 'empty'].map((step) => (
                <FilterListItem
                  key={step}
                  label={step === 'empty' ? 'Vide' : getReadableAnimalAdoptionStep(step as AnimalAdoptionDtoStep)}
                  isSelected={stepFilter.includes(step as AnimalAdoptionDtoStepWithEmpty)}
                  toggleSelected={() => {
                    if (stepFilter.includes(step as AnimalAdoptionDtoStepWithEmpty)) {
                      setStepFilter(stepFilter.filter((aStep) => aStep !== step))
                    } else {
                      setStepFilter([...stepFilter, step as AnimalAdoptionDtoStepWithEmpty])
                    }
                  }}
                />
              ))}
            </FilterList>
          )}
          {adoptionStatusFilter.includes('cannot-be-adopted') && (
            <FilterList label="Raison non-adoption" icon={<PersonPin />}>
              {[...Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus), 'empty'].map((status) => (
                <FilterListItem
                  key={status}
                  label={
                    status === 'empty'
                      ? 'Vide'
                      : getReadableCannotBeAdoptedStatus(status as AnimalAdoptionDtoCannotBeAdoptedStatus)
                  }
                  isSelected={cannotBeAdoptedStatusFilter.includes(
                    status as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty
                  )}
                  toggleSelected={() => {
                    if (
                      cannotBeAdoptedStatusFilter.includes(status as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty)
                    ) {
                      setCannotBeAdoptedStatusFilter(
                        cannotBeAdoptedStatusFilter.filter((aStatus) => aStatus !== status)
                      )
                    } else {
                      setCannotBeAdoptedStatusFilter([
                        ...cannotBeAdoptedStatusFilter,
                        status as AnimalAdoptionDtoCannotBeAdoptedStatusWithEmpty,
                      ])
                    }
                  }}
                />
              ))}
            </FilterList>
          )}
        </FilterList>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <AnimalExportPopUp />
        </Box>
        <AQuestion />
      </>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        padding: 1,
        paddingBottom: 7.5,
        backgroundColor: 'grey.100',
        minHeight: `calc(100% - 20px)`,
        boxSizing: 'border-box',
        marginBottom: 2,
        maxHeight: `calc(100% - 20px)`,
      }}
    >
      {animalStore.isLoading && !animalStore.areAnimalsInitialized ? (
        <CircularProgressPanel />
      ) : (
        <>
          {/* Filters for desktop version */}
          <Card
            sx={{
              ...spacingItem,
              width: 230,
              display: { xs: 'none', sm: 'block' },
              overflowY: 'auto',
            }}
          >
            <CardContent>
              <FiltersBar />
            </CardContent>
          </Card>

          {/* Filters for mobile version */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
            }}
          >
            <Box sx={{ padding: 2 }}>
              <FiltersBar />
            </Box>
          </Drawer>

          <Card
            sx={{
              ...spacingItem,
              marginLeft: { xs: 0, sm: 2 },
              width: '100%',
              maxHeight: '100%',
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                marginBottom: '1rem',
                borderBottom: '1px solid lightgray',
              }}
            >
              <Tab
                label="Tous"
                value="all"
                sx={{
                  textTransform: 'none',
                  borderRadius: '5px 5px 0 0',
                  border: '1px solid lightgray',
                  borderBottom: 'none',
                  marginRight: '5px',
                  marginBottom: '-1px',
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: 'black',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
              <Tab
                label="Animaux suivis"
                value="followed"
                sx={{
                  textTransform: 'none',
                  borderRadius: '5px 5px 0 0',
                  border: '1px solid lightgray',
                  borderBottom: 'none',
                  marginRight: '5px',
                  marginBottom: '-1px',
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: 'black',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
              <Tab
                label="Statut à compléter"
                value="to-be-filled"
                sx={{
                  textTransform: 'none',
                  borderRadius: '5px 5px 0 0',
                  border: '1px solid lightgray',
                  borderBottom: 'none',
                  marginRight: '5px',
                  marginBottom: '-1px',
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: 'black',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
              <Tab
                label="Animaux archivés"
                value="archived"
                sx={{
                  textTransform: 'none',
                  borderRadius: '5px 5px 0 0',
                  border: '1px solid lightgray',
                  borderBottom: 'none',
                  marginRight: '5px',
                  marginBottom: '-1px',
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: 'black',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
            </Tabs>
            <Typography sx={{ position: 'absolute', top: 10, right: 10, display: { xs: 'none', sm: 'block' } }}>{`${
              JSON.stringify(createFilterObject()) === JSON.stringify(baseFilterObject) ? 'Total' : 'Sélectionné(s)'
            }: ${animalStore.animalsCount}`}</Typography>
            {assoHasAnimals && (
              <Box sx={{ width: '100%' }}>
                <Box display="flex" flexDirection="row" flexWrap="nowrap" gap={1} alignItems="center">
                  <TextField
                    id="name-input"
                    placeholder="Rechercher un animal (nom, nº d'immatriculation, membres...)"
                    type="text"
                    fullWidth
                    value={searchQuery}
                    onChange={onChangeSearch}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        handleApplySearch()
                      }
                    }}
                    sx={{
                      borderRadius: 0,
                      [`& fieldset`]: {
                        borderRadius: 0,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment:
                        searchQuery !== '' ? (
                          <InputAdornment position="end">
                            <IconButton aria-label="Effacer la recherche" size="small" onClick={handleClearSearch}>
                              <Close fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => handleApplySearch()}
                    disabled={!isSearchDirty}
                    startIcon={<SearchIcon />}
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      borderRadius: 0,
                      whiteSpace: 'nowrap',
                      height: 56,
                    }}
                  >
                    Rechercher
                  </Button>
                  <IconButton
                    color="inherit"
                    aria-label="Lancer la recherche"
                    onClick={() => handleApplySearch()}
                    disabled={!isSearchDirty}
                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    <SearchIcon />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ display: { sm: 'none' } }}
                  >
                    <Tune />
                  </IconButton>
                </Box>
                {hasAppliedSearch && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      Recherche active : {appliedSearchQuery}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      color="secondary"
                      onClick={handleClearSearch}
                      startIcon={<Close fontSize="small" />}
                      sx={{ textTransform: 'none' }}
                    >
                      Effacer la recherche
                    </Button>
                  </Box>
                )}
                {isSearchDirty && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    Appuyez sur Entrée ou cliquez sur Rechercher pour lancer la recherche.
                  </Typography>
                )}
              </Box>
            )}
            <Box
              sx={{
                width: '100%',
                maxHeight: '100%',
                boxSizing: 'border-box',
                overflow: 'auto',
                paddingBottom: 7, // HACK: To avoid the last row to be hidden
                transform: 'translateZ(0)',
              }}
              ref={scrollRef}
            >
              {assoHasAnimals ? (
                animalStore.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <AnimalsTable
                      animals={animalStore.animals}
                      setSortColumn={setSortColumn}
                      setSortDirection={setSortDirection}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      disableFrontSorting={true}
                      selectedAnimals={selectedAnimals}
                      setSelectedAnimals={setSelectedAnimals}
                    />

                    <Pagination
                      count={Math.ceil(animalStore.animalsCount / maxAnimalPerPage)}
                      page={page}
                      onChange={(_, page) => {
                        setPage(page)
                      }}
                      sx={{ display: 'flex', justifyContent: 'center', marginTop: 2, paddingBottom: 2, mb: 8 }}
                    />
                  </>
                )
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    p: { xs: 4, md: 10 },
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{ fontFamily: 'Insaniburger', color: blueSky, fontSize: { xs: 28, md: 32 } }}
                  >
                    Bienvenue sur Petso !
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: 20, md: 21 } }}>
                    Vous disposez à présent d&#39;un espace gratuit dédié à votre association.
                  </Typography>
                  {/* Video tutorial prompt hidden */}
                  <Typography sx={{ fontWeight: 500, fontSize: { xs: 19, md: 19.5 }, lineHeight: 1.35 }}>
                    Petso a été conçu en collaboration avec des associations et refuges pour faciliter la prise en
                    charge et le suivi de vos animaux. Petso vous permet d&apos;assurer :
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 2,
                      justifyContent: 'left',
                      alignItems: 'stretch',
                      mt: 4,
                      mb: { xs: 10, md: 0 },
                    }}
                  >
                    <SuiviCard
                      title="Membres et FA"
                      subtitle="Petso facilite la collaboration entre membres, familles d'accueil et vétérinaires"
                      description="Ajoutez vos membres, configurez des niveaux de permissions différents, enregistrez leurs informations et rattachez les aux animaux dont-ils sont en charge."
                      link={PATHS.ajouterBenevole.absolute}
                      buttonText="Ajouter un membre"
                    />
                    <SuiviCard
                      title="Animaux"
                      subtitle="Ajoutez vos animaux et complétez leurs fiches pour faciliter leur suivi"
                      description="Renseignez leurs antécédents de santé, centralisez leurs documents, bénéficiez de rappels de santé, éditez en un clic registres et bons vétérinaire."
                      link={PATHS.ajouterAnimal.absolute}
                      buttonText="Ajouter un animal"
                    />
                    <SuiviCard
                      title="Adoptants"
                      subtitle="Petso facilite la collaboration entre membre, famille d'accueil et vétérinaire"
                      description="Ajoutez vos membres, configurez des niveaux de permissions différents, enregistrez leurs informations et rattachez les aux animaux dont ils sont en charge."
                      link={PATHS.ajouterAdoptant.absolute}
                      buttonText="Ajouter un adoptant"
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {account &&
              accountCanEdit(account) &&
              (selectedAnimals.length > 0 ? (
                <Box
                  sx={{
                    position: 'fixed',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    right: 2,
                    bottom: 0,
                    margin: 2,
                  }}
                >
                  <Fab
                    color="secondary"
                    variant="extended"
                    onClick={() =>
                      navigate(PATHS.multiEdition.absolute, {
                        state: {
                          selectedAnimals: JSON.stringify(
                            selectedAnimals.map((a) => ({
                              value: a.id,
                              label: a.name,
                              species: a.species,
                              adoptionStatus: a.adoptionStatus,
                            }))
                          ),
                        },
                      })
                    }
                  >
                    Modifier les animaux sélectionnés ({selectedAnimals.length})
                  </Fab>
                </Box>
              ) : (
                <Box
                  sx={{
                    position: 'fixed',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    right: 2,
                    bottom: 0,
                    margin: 2,
                  }}
                >
                  {accountsStore.isAdmin() && <AnimalSheetPopUp />}
                  <Fab color="primary" variant="extended" onClick={() => navigate(PATHS.ajouterAnimal.absolute)}>
                    <Add sx={{ mr: 1 }} />
                    Ajouter un animal
                  </Fab>
                </Box>
              ))}
          </Card>
        </>
      )}
    </Box>
  )
}

interface SuiviCardProps {
  title: string
  subtitle: string
  description: string
  link: string
  buttonText: string
}

const SuiviCard: React.FC<SuiviCardProps> = ({ title, subtitle, description, link, buttonText }) => {
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        flex: '1 1 300px',
        maxWidth: { xs: '100%', md: '32%' },
        width: '100%',
        mb: { xs: 10, md: 0 },
      }}
    >
      <Typography variant="h2" sx={{ fontFamily: 'Insaniburger', color: blueSky, fontSize: { xs: 24, md: 30 } }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 }, lineHeight: 1.35 }}>{subtitle}</Typography>
        <Typography sx={{ fontWeight: 500, fontSize: { xs: 15, md: 16 }, lineHeight: 1.4 }}>{description}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(link)}
          disabled={!accountsStore.isAdmin()}
          sx={{
            width: { xs: '80%', md: '70%' },
            height: { xs: 42, md: 46 },
            mt: 2,
            borderRadius: 3,
            fontSize: { xs: 14, md: 15 },
            whiteSpace: 'nowrap',
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  )
}
